<template>
  <div>
    <provisioning-status @refresh="load" />
    <v-card class="main-card">
      <v-card-title class="screen-title">
        <span>パケットフィルタリング一覧</span>
        <manual-button path="/packet-filtering-list/" />
      </v-card-title>
      <v-card-text class="pa-2" v-if="isLoaded">
        <div class="d-flex justify-end mb-2">
          <v-btn color="primary" @click="contractModification">契約変更</v-btn>
        </div>
        <v-tabs v-model="tabValue" grow hide-slider>
          <v-tab text="VNコネクト" value="vn"></v-tab>
          <v-tab text="アクセス回線" value="ac" v-if="acLineMenuFound"></v-tab>
          <v-tab v-else disabled></v-tab>
        </v-tabs>
        <v-tabs-window v-model="tabValue">
          <v-tabs-window-item value="vn">
            <v-row no-gutters
              ><span class="text-h6 font-weight-bold pb-4 pt-4"
                >VNコネクト一覧</span
              ></v-row
            >
            <v-row no-gutters>
              <v-col>
                <app-search
                  :search="vnConnect.search"
                  v-model="vnConnect.filter"
                />
              </v-col>
              <!-- パケットフィルタリング契約が未契約の場合非表示 -->
              <div
                class="d-flex flex-row ga-1 flex-wrap ml-2"
                v-if="contractStatus"
              >
                <v-btn color="primary" @click="downloadCsv"
                  >一覧ダウンロード</v-btn
                >
              </div>
            </v-row>
            <div class="d-flex flex-row justify-end mb-2">
              <v-btn color="primary" @click="loadVnCon">一覧更新</v-btn>
            </div>
            <app-table
              v-model:table-sort-by="vnConnect.sortBy"
              primary-key="wnumber"
              :fields="vnConnect.fields"
              :items="vnConnect.items"
              :filter-option="vnConnect.filter_option"
              :filter="vnConnect.filter"
              :hover="true"
              sticky-header="40vh"
              @click:row="showVnConDetails"
              :fixed="true"
            >
              <template #cell(siteName)="data">
                {{ $filter.emptyTo(data.item.siteName, "-") }}
              </template>
              <template #cell(menu)="data">
                {{ $filter.enumTo(data.item.menu, "vnConPktFilterMenu") }}
              </template>
            </app-table>
          </v-tabs-window-item>
          <v-tabs-window-item value="ac">
            <v-row no-gutters
              ><span class="text-h6 font-weight-bold pb-4 pt-4"
                >アクセス回線一覧</span
              ></v-row
            >
            <v-row no-gutters>
              <v-col>
                <app-search :search="acLine.search" v-model="acLine.filter" />
              </v-col>
            </v-row>
            <div class="d-flex flex-row justify-end mb-2">
              <v-btn color="primary" @click="loadAcLine">一覧更新</v-btn>
            </div>
            <app-table
              v-model:table-sort-by="acLine.sortBy"
              primary-key="enumber"
              :fields="acLine.fields"
              :items="acLine.items"
              :filter="acLine.filter"
              :filter-option="acLine.filter_option"
              :hover="true"
              @click:row="showAcLineDetails"
              sticky-header="40vh"
            >
              <template #cell(menu)="data">
                {{ $filter.enumTo(data.item.menu, "acLinePktFilterMenu") }}
              </template>
            </app-table>
          </v-tabs-window-item>
        </v-tabs-window>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type {
  AcLinePktFilterEntity,
  PacketFilterEntity,
  VnConnectPktFilterEntity,
} from "@/apis/PacketFilteringApi";
import PacketFilteringReference from "@/modals/packetFiltering/PacketFilteringReference.vue";
import type {
  AppTableHeader,
  FilterOption,
  SortItem,
} from "@/components/AppTable.vue";
import type { SearchOption } from "@/components/AppSearch.vue";
import PacketFilteringContractReference from "@/modals/packetFiltering/PacketFilteringContractReference.vue";
import ApiNotFoundModal from "@/modals/error/ApiNotFoundModal.vue";

export default defineComponent({
  name: "PacketFilteringList",
  data() {
    return {
      tabValue: "vn",
      vnConMenuItems: vnConMenuItems,
      acLineMenuItems: acLineMenuItems,
      statusItems: statusItems,
      contractStatus: false,
      vnConnect: {
        sortBy: {
          key: "wnumber",
          order: "desc",
        } as SortItem,
        fields: [
          {
            key: "wnumber",
            title: "VNコネクト番号",
            sortable: true,
            width: "200px",
          },
          {
            key: "vnConnectName",
            title: "VNコネクト名",
            sortable: true,
            width: "200px",
          },
          {
            key: "enumberAct",
            title: "回線番号(E番号)",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (
              _v: string,
              _k: string,
              item: VnConnectPktFilterEntity
            ) => {
              return item.menu === "PF_ACCESS_GW2" && item.enumberSby
                ? `${item.enumberAct}/${item.enumberSby}`
                : item.enumberAct;
            },
            width: "200px",
          },
          {
            key: "menu",
            title: "メニュー",
            width: "300px",
          },
          {
            key: "vlan",
            title: "VLAN",
            align: "end",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: true,
            formatter: (
              _v: string,
              _k: string,
              item: VnConnectPktFilterEntity
            ) => {
              if (item.vlanIdType)
                return item.vlanIdType === "SPECIFIED"
                  ? `${item.vlanId}`
                  : this.$filter.enumTo(item.vlanIdType, "vlanIdType");
            },
            width: "120px",
          },
          {
            key: "dstVpnVnCode",
            title: "接続先VPN/VNコード",
            sortable: true,
            filterByFormatted: true,
            formatter: (
              _v: string,
              _k: string,
              item: VnConnectPktFilterEntity
            ) => {
              return item.vnName
                ? `${item.vnName}(${item.dstVpnVnCode})`
                : item.dstVpnVnCode;
            },
            width: "300px",
          },
          {
            key: "siteName",
            title: "サイト名",
            sortable: true,
            width: "200px",
          },
          {
            key: "filterringSettingStatus",
            title: "パケットフィルタリング設定有無",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) =>
              this.$filter.enumTo(value, "filterringSettingStatus"),
            width: "340px",
          },
        ] as AppTableHeader<VnConnectPktFilterEntity>[],
        items: [] as VnConnectPktFilterEntity[],
        search: {
          wnumber: { type: "text", label: "VNコネクト番号" },
          vnConnectName: { type: "text", label: "VNコネクト名" },
          enumberAct: { type: "text", label: "回線番号(E番号)" },
          menu: { type: "select", label: "メニュー", items: vnConMenuItems },
          vlan: { type: "text", label: "VLAN" },
          dstVpnVnCode: { type: "text", label: "接続先VPN/VNコード" },
          siteName: { type: "text", label: "サイト名" },
          filterringSettingStatus: {
            type: "select",
            label: "パケットフィルタリング設定有無",
            items: statusItems,
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          wnumber: "like",
          vnConnectName: "like",
          enumberAct: "like",
          menu: "equal",
          vlan: "like",
          dstVpnVnCode: "like",
          siteName: "like",
          filterringSettingStatus: "equal",
        } as FilterOption,
      },
      acLine: {
        sortBy: {
          key: "enumber",
          order: "desc",
        } as SortItem,
        fields: [
          {
            key: "enumber",
            title: "回線番号(E番号)",
            sortable: true,
            width: "240px",
          },
          {
            key: "menu",
            title: "メニュー",
            width: "300px",
          },
          {
            key: "filterringSettingStatus",
            title: "パケットフィルタリング設定有無",
            sortable: true,
            sortByFormatted: true,
            filterByFormatted: false,
            formatter: (value: string) =>
              this.$filter.enumTo(value, "filterringSettingStatus"),
            width: "340px",
          },
        ] as AppTableHeader<AcLinePktFilterEntity>[],
        items: [] as AcLinePktFilterEntity[],
        selected: [] as AcLinePktFilterEntity[],
        search: {
          enumber: { type: "text", label: "回線番号(E番号)" },
          menu: { type: "select", label: "メニュー", items: acLineMenuItems },
          filterringSettingStatus: {
            type: "select",
            label: "パケットフィルタリング有無",
            items: statusItems,
          },
        } as SearchOption,
        filter: {} as Record<string, unknown>,
        filter_option: {
          enumber: "like",
          menu: "equal",
          filterringSettingStatus: "equal",
        } as FilterOption,
      },
      vnConnectPackets: {
        items: [] as PacketFilterEntity[],
      },
      acLinePackets: {
        items: [] as PacketFilterEntity[],
      },
      vnConnectPacketFilterKeyId: "",
      accessLinePacketFilterKeyId: "",
      isLoaded: false,
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  computed: {
    acLineMenuFound(): boolean {
      // 表示:契約回線に「エクステンドイーサ/IPsecGW/プラットフォームGW/リモートアクセスGW/イーサネット方式(L3)/ブロードバンドアクセス方式Ⅱ(L3)/ワイヤレスアクセス方式(L3)」がある場合
      // 非表示：契約回線に「エクステンドイーサ/IPsecGW/プラットフォームGW/リモートアクセスGW/イーサネット方式(L3)/ブロードバンドアクセス方式Ⅱ(L3)/ワイヤレスアクセス方式(L3)」がない場合
      return this.$store.state.user.contractInfo.contractList.some((v) => {
        switch (v.accessType) {
          case "EXTEND_ETHERNET":
          case "IPSEC":
          case "PLATFORM_GATEWAY":
          case "REMOTE_ACCESS_GW_CPA":
          case "REMOTE_ACCESS_GW_CRG":
          case "REMOTE_ACCESS_GW_FRE":
          case "ETHERNET_L3":
          case "BROADBAND_ACCESS2_L3":
          case "WIRELESS_ACCESS_L3":
            return true;
          default:
            return false;
        }
      });
    },
  },
  methods: {
    async load() {
      await this.loadContract();
      await this.loadVnCon();
      if (this.acLineMenuFound) {
        await this.loadAcLine();
      }
    },
    /** パケットフィルタリング契約情報を取得 */
    async loadContract() {
      const contractData =
        await this.$api.packetFilter.getContractPacketFiltering();
      this.contractStatus = contractData.packetFilterEnabled;
    },
    /** VNコネクト一覧更新 */
    async loadVnCon() {
      /** パケットフィルタ用VNコネクト一覧を取得 */
      const vnConnectData = await this.$api.packetFilter.getVnConnects();
      this.vnConnect.items = vnConnectData.packetFilterList;
      this.vnConnectPacketFilterKeyId =
        vnConnectData.vnConnectPacketFilterKeyId;
    },
    /** AC回線一覧更新 */
    async loadAcLine() {
      const acLineData = await this.$api.packetFilter.getAcLines();
      this.acLine.items = acLineData.packetFilterList;
      this.accessLinePacketFilterKeyId = acLineData.accessLinePacketFilterKeyId;
    },
    // パケットフィルタリング設定情報表示（VNコネクト）
    async showVnConDetails(item: VnConnectPktFilterEntity) {
      await this.loadVnConDetails(item);
      await this.$modal.show(PacketFilteringReference, {
        packetFilteringItem: this.vnConnectPackets.items,
        clickRowVnConItem: item,
        packetFilterKeyId: this.vnConnectPacketFilterKeyId,
      });
      await this.load();
    },
    async loadVnConDetails(item: VnConnectPktFilterEntity) {
      // wnumberに対してパケットフィルタリングルール一覧情報取得
      const data = await this.$api.packetFilter.getVnConnectPktFilter(
        item.wnumber
      );
      this.vnConnectPackets.items = data.packetFilterRuleList;
    },
    // パケットフィルタリング設定情報表示（アクセス回線）
    async showAcLineDetails(item: AcLinePktFilterEntity) {
      await this.loadAcLineDetails(item);
      await this.$modal.show(PacketFilteringReference, {
        packetFilteringItem: this.acLinePackets.items,
        clickRowAcLineItem: item,
        packetFilterKeyId: this.accessLinePacketFilterKeyId,
      });
      await this.load();
    },
    async loadAcLineDetails(item: AcLinePktFilterEntity) {
      // enumberに対してパケットフィルタリング一覧情報取得
      const data = await this.$api.packetFilter.getAcLinePktFilter(
        item.enumber
      );
      this.acLinePackets.items = data.packetFilterRuleList;
    },
    async contractModification() {
      await this.$modal.show(PacketFilteringContractReference);
    },
    /** 一覧ダウンロード */
    async downloadCsv() {
      await this.loadContract();
      if (this.contractStatus) {
        window.open(this.$api.packetFilter.getCsvURL(), "_blank");
      } else {
        // パケットフィルタリング契約が「未契約」の場合はエラーモーダルを表示
        await this.$modal.show(ApiNotFoundModal);
      }
    },
  },
});

/** VNコネクト一覧メニュー */
export const vnConMenuItems = [
  { value: "ETHERNET2", text: "イーサネット方式Ⅱ" },
  { value: "ETHERNET2_PF", text: "イーサネット方式Ⅱ PF" },
  { value: "BROADBAND_ACCESS3", text: "ブロードバンドアクセス方式Ⅲ" },
  { value: "WIRELESS_ACCESS2", text: "ワイヤレスアクセス方式Ⅱ" },
  { value: "PF_ACCESS_GW2", text: "PFアクセスGWⅡ" },
  { value: "ETHERNET_LIGHT", text: "イーサネットライト" },
];

/** アクセス回線一覧メニュー */
export const acLineMenuItems = [
  { value: "ETHERNET", text: "イーサネット方式" },
  { value: "BROADBAND_ACCESS2", text: "ブロードバンドアクセス方式Ⅱ" },
  { value: "WIRELESS_ACCESS", text: "ワイヤレスアクセス方式" },
  { value: "EXTEND_ETHERNET", text: "エクステンドイーサ方式" },
  { value: "IPSEC", text: "IPsec方式" },
  { value: "PLATFORM_GATEWAY", text: "PFGW" },
  { value: "REMORE_ACCESS_GW", text: "リモートアクセスGW" },
];

/** パケットフィルタリング設定有無（検索窓選択肢用） */
export const statusItems = [
  { value: "FILTERING", text: "有り" },
  { value: "NO_FILTERING", text: "無し" },
];
</script>
